import React from 'react';
import clsx from 'clsx';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /** primary = orange CTA · secondary = plum · outline / ghost = quiet · danger */
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
    /** 36 / 46 / 56 px tall */
    size?: 'sm' | 'md' | 'lg';
    fullWidth?: boolean;
    loading?: boolean;
    icon?: React.ReactNode;
    iconRight?: React.ReactNode;
}

const variantStyles: Record<NonNullable<ButtonProps['variant']>, string> = {
    primary: 'bg-accent-500 text-ink-onAccent shadow-accent hover:bg-accent-600 hover:-translate-y-px',
    secondary: 'bg-primary-500 text-white shadow-brand hover:bg-primary-700 hover:-translate-y-px',
    outline: 'bg-transparent text-ink-brand border border-line hover:border-primary-500 hover:bg-brandsoft',
    ghost: 'bg-transparent text-ink-brand hover:bg-brandsoft',
    danger: 'bg-status-danger text-white hover:brightness-95 hover:-translate-y-px',
};

const sizeStyles: Record<NonNullable<ButtonProps['size']>, string> = {
    sm: 'h-9 px-4 text-sm gap-1.5',
    md: 'h-[46px] px-[22px] text-base gap-2',
    lg: 'h-14 px-[30px] text-lg gap-2',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ variant = 'primary', size = 'md', fullWidth = false, loading = false, disabled = false, icon, iconRight, children, className, type = 'button', ...props }, ref) => (
        <button
            ref={ref}
            type={type}
            disabled={disabled || loading}
            className={clsx(
                'inline-flex items-center justify-center whitespace-nowrap rounded-pill font-extrabold tracking-[-0.01em]',
                'transition-[transform,background-color,box-shadow,filter] duration-200 active:scale-[.97]',
                'disabled:opacity-45 disabled:pointer-events-none',
                variantStyles[variant],
                sizeStyles[size],
                fullWidth && 'w-full',
                className,
            )}
            {...props}
        >
            {loading ? <Spinner /> : icon && <span className="inline-flex shrink-0 [&>svg]:w-[18px] [&>svg]:h-[18px]">{icon}</span>}
            {children}
            {!loading && iconRight && <span className="inline-flex shrink-0 [&>svg]:w-[18px] [&>svg]:h-[18px]">{iconRight}</span>}
        </button>
    ),
);
Button.displayName = 'Button';

export const Spinner: React.FC<{ className?: string }> = ({ className }) => (
    <span
        aria-hidden
        className={clsx('inline-block w-[18px] h-[18px] rounded-full border-[3px] border-current/25 border-t-current animate-spin', className)}
    />
);

export default Button;
